import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { CreatePostReplyApiProps, ReplyEntry, UpdatePostReplyApiProps } from "../../../../types/support/board";
import { useModal } from "../../../../hooks";
import useFileHandler from "../../../../hooks/useFileHandler";
import { createPostReplyApi, deletePostReplyApi, updatePostReplyApi } from "../../../../api/support/board";

export const DefaultInputData = {
    postId: '',
    postReplyId: '',
    userId: 0,
    userName: '',
    body: '',
    regAt: '',
    isMain: 'N',
    fileList: [],
    fileVOList: [],
    fileKeyList: [],
    replyList: []
};

interface EditPostReplyProps {
    data?: ReplyEntry;
    onCanceled?: (key: string) => void;
    postId: string;
}

/**
 * 게시판 reply CRUD hook
 * @returns
 */
export const useEditPostReply = ({ data, onCanceled, postId }: EditPostReplyProps) => {
    const queryClient = useQueryClient();
    const { handleModal } = useModal();
    const [inputData, setInputData] = useState<ReplyEntry>(data ?? DefaultInputData);
    const { removeFile, handleButtonClick, UploadUI } = useFileHandler({ setter: setInputData });

    const deleteMutation = useMutation({
        mutationFn: (data: string) => deletePostReplyApi(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({
                queryKey: ["readPostDetailApi", postId],
            });
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '삭제되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const updateMutation = useMutation({
        mutationFn: (data: UpdatePostReplyApiProps) => updatePostReplyApi(data),
        onSuccess: (res, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["readPostDetailApi", postId],
            });
            onCanceled && onCanceled(variables.postReplyId);
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '수정되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const createMutation = useMutation({
        mutationFn: (data: CreatePostReplyApiProps) => createPostReplyApi(data),
        onSuccess: (res) => {
            queryClient.invalidateQueries({
                queryKey: ["readPostDetailApi", postId],
            });
            onCanceled && onCanceled(res.result.postReplyId);
            handleModal({
                isOpen: true,
                type: 'alert',
                contents: '저장되었습니다.',
            });
        },
        onError: (error) => {
            console.log("update error", error);
        },
    });

    const deletePostReply = (data: string) => {
        handleModal({
            isOpen: true,
            type: 'confirm',
            contents: "선택한 답글을 삭제하시겠습니까?",
            title: "알림",
            callback: () => {
                deleteMutation.mutate(data);
            }
        });
    };

    const updatePostReply = (data: UpdatePostReplyApiProps) => {
        updateMutation.mutate(data);
    }

    const createPostReply = (data: CreatePostReplyApiProps) => {
        createMutation.mutate(data);
    }

    const onChangeInputData = (
        e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
    };

    useEffect(() => {
        setInputData(data ?? DefaultInputData);
    }, [data]);

    return {
        deletePostReply,
        updatePostReply, createPostReply,
        inputData,
        onChangeInputData,
        setInputData,
        UploadUI,
        handleButtonClick,
        removeFile
    };
};
